import { Component, Fragment, h, Host, Prop } from '@stencil/core';
import { ItemDetails, TimeSlotPicker, Translations } from '../../../redux/types';
import { DaysAvailability, Price, SelectStyle, WidgetType } from '../../../types/types';
import { trans } from '../../../utils/trans';
import { ButtonAppearance, ButtonControlValues, ButtonSize, ButtonStyle } from '../../atoms/rentware-button/types';

@Component({
  tag: 'rentware-item-template-range',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RentwareItemTemplateRange {
  @Prop() itemId: string;
  @Prop() itemDetails: ItemDetails;
  @Prop() isThisItemInCart: boolean;
  @Prop() loading: boolean;
  @Prop() calendarLoading: boolean;
  @Prop() orderReference: string;
  @Prop() translations: Translations;
  @Prop() locale: string;
  @Prop() withTime: boolean;
  @Prop() withDateRange: boolean;
  @Prop() selectedLocationId?: string;
  @Prop() selectedVariationId?: string;
  @Prop() selectedMileageId?: string;
  @Prop() notAvailable: boolean;
  @Prop() amountToAdd: number;
  @Prop() price: Price;
  @Prop() daysAvailability: DaysAvailability;
  @Prop() firstAvailableDate: Date;
  @Prop() lastAvailableDate: Date;
  @Prop() onCheckoutClick: () => void;
  @Prop() onSubmit: () => void;
  @Prop() onLocationChange: (id: string) => void;
  @Prop() onVariationChange: (id: string) => void;
  @Prop() onMileageChange: (id: string) => void;
  @Prop() onChangeAmountToBook: (quantity: number) => void;

  @Prop() loadingPickUpHours: boolean;
  @Prop() pickUpDate: Date;
  @Prop() pickUpTime: string;
  @Prop() pickUpHours: string[];
  @Prop() onPickUpTimeChange: (hour: string) => void;

  @Prop() loadingDropOffHours: boolean;
  @Prop() dropOffDate: Date;
  @Prop() suspectedDropOffDate: Date;
  @Prop() dropOffTime: string;
  @Prop() dropOffHours: string[];
  @Prop() onDropOffTimeChange: (hour: string) => void;

  @Prop() timeSlotPicker: TimeSlotPicker;

  @Prop() toggleVariations: () => void;
  @Prop() toggleMileage: () => void;
  @Prop() onRangeDayClick: (day: Date) => void;
  @Prop() onRangeMonthClick: (month: Date) => void;
  @Prop() onResetCalendar: (day?: Date) => void;

  @Prop() buttonText: string;

  get globalStyle() {
    return (
      (!!this.orderReference && 'CalendarTemplateContainer CalendarTemplateContainerDimensions BgDark') || 'CalendarTemplateContainer CalendarTemplateContainerDimensions BgWhite'
    );
  }

  get availabilityTitle() {
    return trans('articleBooking.availability', this.locale, this.translations);
  }
  get bookingTitle() {
    return trans('articleBooking.title', this.locale, this.translations);
  }

  get timeHint() {
    return trans('articleBooking.chooseHint', this.locale, this.translations);
  }

  get timeSlotHint() {
    return trans('articleBooking.timeSlotHint', this.locale, this.translations);
  }

  get pickUpLabel() {
    return trans('articleBooking.pickUp', this.locale, this.translations);
  }

  get dropOffLabel() {
    return trans('articleBooking.dropOff', this.locale, this.translations);
  }

  get timeSlotLabel() {
    return trans('articleBooking.timeSlotLabel', this.locale, this.translations);
  }

  get notAvailableTitle() {
    return trans('articleBooking.notAvailableTitle', this.locale, this.translations);
  }

  get notAvailableText() {
    return trans('articleBooking.notAvailableText', this.locale, this.translations);
  }

  get checkoutButtonText() {
    return trans('cart.buttonOpenCheckout', this.locale, this.translations);
  }

  get backToArticleText() {
    return trans('articleBooking.backToArticle', this.locale, this.translations);
  }

  get bookingMessage() {
    const oneDayMsg = trans('articleBooking.bookingMessageOneDay', this.locale, this.translations);
    const multiDayMsg = trans('articleBooking.bookingMessage', this.locale, this.translations);

    if (this.timeSlotPicker) {
      if (this.timeSlotPicker.pickerType === WidgetType.FixedDuration) {
        return multiDayMsg;
      } else if (this.timeSlotPicker.pickerType === WidgetType.FixedHours) {
        return oneDayMsg;
      }
    } else {
      if (this.withDateRange) {
        return multiDayMsg;
      } else {
        return oneDayMsg;
      }
    }
  }

  get buttonControlValues(): ButtonControlValues {
    return (
      this.price.available && {
        id: this.itemDetails?.id,
        minQuantity: this.price?.quantity?.min,
        maxQuantity: this.price?.quantity?.max,
        currentQuantity: this.amountToAdd,
        onChange: this.onChangeAmountToBook,
      }
    );
  }

  get buttonAppearance(): ButtonAppearance {
    return this.price.available
      ? {
          fullWidth: true,
          size: ButtonSize.XL,
        }
      : {
          style: ButtonStyle.Inverse,
          fullWidth: true,
          disabled: true,
          size: ButtonSize.XL,
        };
  }

  get dropOffPickerAvailable() {
    return (!!this.pickUpTime && !this.withDateRange) || ((!!this.dropOffDate || !!this.suspectedDropOffDate) && this.pickUpTime && this.withDateRange);
  }

  get showTimePickers() {
    return this.withTime && !this.timeSlotPicker;
  }

  get showNotAvailableOverlay(): boolean {
    const componentLoading = this.loading || this.calendarLoading || this.loadingPickUpHours || this.loadingDropOffHours;

    return this.notAvailable && !componentLoading;
  }

  get focusFromTime(): boolean {
    if (this.loading || this.calendarLoading || this.loadingPickUpHours || this.loadingDropOffHours) {
      return false;
    }

    if (!!this.timeSlotPicker) {
      return !!this.pickUpDate && !this.timeSlotPicker.selectedOption;
    }

    if (!this.withDateRange) {
      return !!this.pickUpDate && !this.pickUpTime;
    }

    //date range case
    return (
      !!this.pickUpDate && //
      (!!this.dropOffDate || !!this.suspectedDropOffDate) &&
      !this.pickUpTime
    );
  }

  get focusToTime(): boolean {
    if (this.loading || this.calendarLoading || this.loadingPickUpHours || this.loadingDropOffHours) {
      return false;
    }

    return (
      !!this.pickUpDate && //
      (!!this.dropOffDate || !!this.suspectedDropOffDate) &&
      !!this.pickUpTime &&
      !this.dropOffTime
    );
  }

  render() {
    const articleHasLocation = !!this.itemDetails.location
    const allowedLocations = this.itemDetails.allowedLocations ?? []
    const showLocationSelect = !articleHasLocation

    const locationOptions = allowedLocations.map((item) => {
        return {
            label: item.label,
            value: item.id,
        }
    })

    const locationDropdown = (
        <rentware-select
            selectStyle={SelectStyle.Search}
            selectTextKey="articleBooking.selectLocation"
            value={this.selectedLocationId}
            onValueChange={e => {
                this.onLocationChange(e.detail.value)
            }}
            options={locationOptions}
        />
    )

    const locationSelectWrapper = this.selectedLocationId ? locationDropdown :  (<rtr-border-wrapper>{locationDropdown}</rtr-border-wrapper>)

    return (
      <Host class={this.globalStyle}>
        <rentware-spinner style={this.loading ? {} : { display: 'none' }} withOverlay={true} />

        {this.orderReference && (
          <Fragment>
            <rentware-cart-header headerTitle={this.bookingTitle} showTimer={this.isThisItemInCart} />

            <rentware-spacer size="S" />

            <rentware-item-in-cart
              itemId={this.itemId}
              itemDetails={this.itemDetails}
              isThisItemInCart={this.isThisItemInCart}
              onCheckoutClick={this.onCheckoutClick}
              checkoutButtonText={this.checkoutButtonText}
              headerTitle={this.bookingTitle}
              backToArticleText={this.backToArticleText}
            />
          </Fragment>
        )}

        {!this.orderReference && (
          <Fragment>
            <rentware-cart-header headerTitle={this.availabilityTitle} showTimer={this.isThisItemInCart} />

            <rentware-spacer size="S" />

            <div>{this.bookingMessage}</div>

            <rentware-spacer size="M" />

            {showLocationSelect &&
              <Fragment>
                {locationSelectWrapper}
                <rentware-spacer size='M' />
              </Fragment>
            }

            {this.itemDetails?.mileageOptions && (
              <rentware-select-mileage
                mileageOptions={this.itemDetails.mileageOptions}
                available={this.itemDetails.available}
                selectedMileageId={this.selectedMileageId}
                onMileageChange={this.onMileageChange}
              />
            )}

            {!!this.itemDetails?.variation && (
              <rentware-select
                selectStyle={SelectStyle.Variations}
                label={this.itemDetails.variation.name}
                value={this.selectedVariationId}
                onValueChange={e => this.onVariationChange(e.detail.value)}
                options={this.itemDetails.variation.items.map(item => {
                  return {
                    label: item.name,
                    value: item.id,
                    disabled: !item.maxQuantity && !item.quantityInCart,
                  };
                })}
              />
            )}

            {(this.itemDetails?.variation || this.itemDetails?.mileageOptions) && (
              <Fragment>
                <div class="VariationsDivider" />

                <rentware-spacer size="M" />
              </Fragment>
            )}

            <rentware-range-picker
              locale={this.locale}
              onRangeDayClick={this.onRangeDayClick}
              onRangeMonthClick={this.onRangeMonthClick}
              onResetCalendar={this.onResetCalendar}
              withDateRange={this.withDateRange}
              rangeLoading={this.calendarLoading}
              daysAvailability={this.daysAvailability}
              firstAvailableDate={this.firstAvailableDate}
              lastAvailableDate={this.lastAvailableDate}
              bookingDate={this.pickUpDate}
              endDate={this.dropOffDate}
              showNotAvailableOverlay={this.showNotAvailableOverlay}
              showLocationNotSelectedOverlay={!this.itemDetails?.location && !this.selectedLocationId}
            />

            <rentware-spacer size="M" />

            <div class="PickersContainer">
              {this.showTimePickers && (
                <Fragment>
                  <div class="PickerContainer">
                    <rtr-border-wrapper show={this.focusFromTime}>
                      <rentware-time-picker
                        autoClose={this.withDateRange}
                        hint={this.timeHint}
                        label={this.pickUpLabel}
                        value={this.pickUpTime}
                        options={this.pickUpHours}
                        disabled={!this.pickUpDate}
                        onTimeChange={this.onPickUpTimeChange}
                        isLoading={this.loadingPickUpHours}
                        isPlaceholderRed={true}
                      />
                    </rtr-border-wrapper>
                  </div>

                  <div class="PickerContainer">
                    <rtr-border-wrapper show={this.focusToTime}>
                      <rentware-time-picker
                        autoClose={true}
                        hint={this.timeHint}
                        label={this.dropOffLabel}
                        value={this.dropOffTime}
                        options={this.dropOffHours}
                        onTimeChange={this.onDropOffTimeChange}
                        disabled={!this.dropOffPickerAvailable}
                        isLoading={this.loadingDropOffHours}
                        isPlaceholderRed={true}
                      />
                    </rtr-border-wrapper>
                  </div>
                </Fragment>
              )}

              {this.timeSlotPicker && (
                <div class="PickerContainer">
                  <rtr-border-wrapper show={this.focusFromTime}>
                    <rentware-time-picker
                      autoClose={true}
                      hint={this.timeSlotHint}
                      label={this.timeSlotLabel}
                      value={this.timeSlotPicker.selectedOption}
                      optionsWithLabel={this.timeSlotPicker.options}
                      onTimeChange={this.timeSlotPicker.onOptionChange}
                      disabled={!this.pickUpDate}
                      isPlaceholderRed={true}
                    />
                  </rtr-border-wrapper>
                </div>
              )}
            </div>

            <rentware-spacer size="M" />

            {((!this.notAvailable || this.loading) && (
              <rentware-button
                appearance={this.buttonAppearance}
                buttonText={this.buttonText}
                controlValues={this.buttonControlValues}
                onClick={this.onSubmit}
                isWaiting={this.loading}
              />
            )) || (
              <div class="NotAvailableContainer">
                <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9.26795 2C10.0377 0.666667 11.9623 0.666667 12.7321 2L20.5263 15.5C21.2961 16.8333 20.3338 18.5 18.7942 18.5H3.20577C1.66617 18.5 0.70392 16.8333 1.47372 15.5L9.26795 2Z"
                    stroke-width="2"
                  />
                </svg>

                <div class="NotAvailableContainerInner">
                  <span class="Title">{this.notAvailableTitle}</span>
                  <span>{this.notAvailableText}</span>
                </div>
              </div>
            )}
            <rentware-spacer size="S" />

            <rentware-secure secureTitle={this.availabilityTitle} goBackText={this.backToArticleText} />
          </Fragment>
        )}
      </Host>
    );
  }
}
